import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { Alert, Button, TextInput } from '../components/ui.tsx';
import { Turnstile, useAppConfig } from '../components/turnstile.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import AuthShell from '../components/auth-shell.tsx';

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('auth.forgotPassword.documentTitle'));
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileReset, setTurnstileReset] = useState(0);

  const config = useAppConfig();
  const siteKey = config.data?.turnstileSiteKey ?? null;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      // The server's own `result.message` is deliberately not rendered here:
      // it is always the same hardcoded English sentence (spec §6.5 — the
      // same wording whether or not the address is registered), and the
      // Worker has no way to know the browser's locale. This renders an
      // equivalent, translated, equally non-enumerating message instead.
      await authApi.requestPasswordReset({
        email,
        ...(turnstileToken ? { turnstileToken } : {}),
      });
      setSubmitted(true);
    } catch (caught) {
      setTurnstileReset((value) => value + 1);
      setError(caught instanceof ApiRequestError ? caught.message : t('auth.forgotPassword.networkError'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title={t('auth.forgotPassword.title')} subtitle={t('auth.forgotPassword.subtitle')}>
      {submitted ? (
        <Alert tone="success" title={t('auth.forgotPassword.checkInboxTitle')}>
          {t('auth.forgotPassword.successMessage')}
        </Alert>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          {error ? (
            <div className="mb-4">
              <Alert tone="error">{error}</Alert>
            </div>
          ) : null}

          <TextInput
            label={t('auth.forgotPassword.emailLabel')}
            type="email"
            name="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />

          {siteKey ? <Turnstile siteKey={siteKey} onToken={setTurnstileToken} resetSignal={turnstileReset} /> : null}

          <Button type="submit" variant="primary" busy={busy} className="w-full">
            {t('auth.forgotPassword.submit')}
          </Button>
        </form>
      )}

      <p className="mt-6 text-sm">
        <Link to="/login" className="text-[color:var(--color-brand)] hover:underline">
          {t('auth.forgotPassword.backToSignIn')}
        </Link>
      </p>
    </AuthShell>
  );
}
